import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import CustomDatePicker from "../../Common/DatePicker";
import Select from "react-select";
import { MultiSelect } from "react-multi-select-component";
import {
  autoScrollSelectComponents,
  scrollMultiSelectToSelectionOnOpen,
} from "../../Common/SelectMenuScroll";
import { capitalizeFirstLetter } from "../../../utilities/CommonUtilities";

import { Button, Offcanvas } from "react-bootstrap";
import { FaSlidersH } from "react-icons/fa";
import Switch from "react-switch";

const FilterBarMobile = forwardRef(
  (
    {
      filters,
      filterDate,
      setFilterDate,
      selectedFilters,
      setSelectedFilters,
      isLoading,
      dashboardChartData,
      dashboard_name,
      start_date_limit,
      end_date_limit,
      setDashboardGranularity,
      dashboardGranularity,
      availableGranularities,

      showSuggestions,
      setShowSuggestions,
      searchFlag,
      setSearchFlag,
      downloadButtonLoading,
      onDownload,
      isDark,
    },
    ref
  ) => {
    const MAX_FILTER3 = 30;
    const [showSheet, setShowSheet] = useState(false);
    const filter3MenuRef = useRef(null);

    // ✅ Theme (matches your existing dark tokens used elsewhere)
    const theme = useMemo(() => {
      return isDark
        ? {
            pageBg: "#0B1020",
            surface: "#0F1424",
            surface2: "#121A2E",
            border: "#232B4A",
            text: "#E8ECF8",
            muted: "#A6B0CF",
            primary: "#4C7DFF",
            btnBg: "#141C33",
            btnBorder: "#2A3560",
            danger: "#E74C3C",
            divider: "#1E2642",
          }
        : {
            pageBg: "#FFFFFF",
            surface: "#FFFFFF",
            surface2: "#FAFAFA",
            border: "#E9ECEF",
            text: "#0B1220",
            muted: "#5B6472",
            primary: "#0D6EFD",
            btnBg: "#FFFFFF",
            btnBorder: "#DEE2E6",
            danger: "#DC3545",
            divider: "#E9ECEF",
          };
    }, [isDark]);

    // -----------------------
    // Options (same logic)
    // -----------------------
    const filter1Values = useMemo(() => {
      if (!filters) return [];
      return Object.keys(filters).map((x) => ({
        value: x.toUpperCase(),
        label: x
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(" "),
      }));
    }, [filters]);
    const filter2Values = useMemo(() => {
      if (
        filters &&
        selectedFilters?.filter1?.value?.toLowerCase() &&
        filters[selectedFilters.filter1.value.toLowerCase()]
      ) {
        return Object.keys(filters[selectedFilters.filter1.value.toLowerCase()])
          .sort()
          .map((x) => ({
            value: x,
            label: x
              .split(" ")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(" "),
          }));
      }
      return [];
    }, [filters, selectedFilters?.filter1]);
    const filter3Values = useMemo(() => {
      if (
        filters &&
        selectedFilters?.filter1 &&
        selectedFilters?.filter2 &&
        selectedFilters.filter2.length > 0 &&
        filters[selectedFilters.filter1.value.toLowerCase()] &&
        filters[selectedFilters.filter1.value.toLowerCase()][selectedFilters.filter2[0].value]
      ) {
        const values = filters[selectedFilters.filter1.value.toLowerCase()][
          selectedFilters.filter2[0].value
        ]
          .sort()
          .map((x) => ({ value: x, label: x }));

        if (values.length > MAX_FILTER3 && selectedFilters.filter3.length === 0) {
          setSelectedFilters({
            ...selectedFilters,
            filter3: values.slice(0, 1),
          });
        }

        return values;
      }
      return [];
    }, [filters, selectedFilters?.filter1, selectedFilters?.filter2]);
    const granularityOptions = useMemo(
      () =>
        (availableGranularities || []).map((x) => ({ value: x, label: x })),
      [availableGranularities],
    );
    // -----------------------
    // Imperative handle
    // -----------------------
    const filter1Key = selectedFilters?.filter1?.value;

    useImperativeHandle(ref, () => ({
      handleSentimentClick: (company, country) => {
        if (!filter1Key || !filters?.[filter1Key?.toLowerCase()]) return;

        const matchedCountryKey = Object.keys(filters[filter1Key.toLowerCase()]).find(
          (key) => key.toLowerCase() === country.toLowerCase()
        );
        if (!matchedCountryKey) return;

        const matchedCountry = {
          value: matchedCountryKey,
          label: matchedCountryKey
            .split(" ")
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" "),
        };

        const companies = filters[filter1Key.toLowerCase()][matchedCountryKey] || [];

        const matchedCompanyKey = companies.find(
          (key) =>
            key.toLowerCase() === company.toLowerCase() ||
            key.toLowerCase().includes(company.toLowerCase())
        );
        if (!matchedCompanyKey) return;

        const matchedCompany = {
          value: matchedCompanyKey,
          label: matchedCompanyKey
            .split(" ")
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" "),
        };

        setSelectedFilters((prev) => ({
          ...prev,
          filter2: [matchedCountry],
          filter3: [matchedCompany],
        }));

        setShowSheet(true);
      },
    }));

    // -----------------------
    // Handlers
    // -----------------------
    const handleFilter1Change = (value) => {
      setSelectedFilters({
        ...selectedFilters,
        filter1: value,
      });
    };

    const handleFilter2Change = (value) => {
      if (value.length === 0 && filter2Values.length > 0) {
        setSelectedFilters({
          ...selectedFilters,
          filter2: [filter2Values[0]],
          filter3: [],
        });
      } else {
        setSelectedFilters({
          ...selectedFilters,
          filter2: value,
          filter3: [],
        });
      }
    };

    const handleFilter3Change = (value) => {
      const dedup = Array.from(new Map(value.map((v) => [v.value, v])).values());

      if (dedup.length === 0 && filter3Values.length > 0) {
        setSelectedFilters({
          ...selectedFilters,
          filter3: [filter3Values[0]],
        });
        return;
      }

      if (dedup.length > MAX_FILTER3) {
        toast.error(`You can select up to ${MAX_FILTER3} selections.`);
        setSelectedFilters({
          ...selectedFilters,
          filter3: dedup.slice(0, MAX_FILTER3),
        });
        return;
      }

      setSelectedFilters({
        ...selectedFilters,
        filter3: dedup,
      });
    };

    const handleGranularityChange = (value) => setDashboardGranularity(value);

    const customValueRenderer = (selected, _options) => {
      const isCompany = dashboard_name?.toLowerCase().includes("company");
      const isGeo =
        dashboard_name?.toLowerCase().includes("macro") ||
        dashboard_name?.toLowerCase().includes("frontier");

      if (_options.length === 0) return "No options available";
      if (selected.length === _options.length) {
        if (isCompany) return "All Companies";
        if (isGeo) return "All Themes";
        return "All Themes";
      }
      if (selected.length === 0) {
        if (isCompany) return "All Companies";
        if (isGeo) return "All Themes";
        return "All Themes";
      }
      if (selected.length === 1) return selected[0].label;
      return selected.map(({ label }) => label).join(", ");
    };

    // -----------------------
    // react-select styles (dark aware)
    // -----------------------
    const selectPortalStyles = {
      menuPortal: (base) => ({ ...base, zIndex: 99999 }),
      menu: (base) => ({
        ...base,
        zIndex: 99999,
        backgroundColor: theme.surface,
        border: `1px solid ${theme.border}`,
        color: theme.text,
      }),
      control: (base, state) => ({
        ...base,
        minHeight: "46px",
        borderRadius: "12px",
        backgroundColor: theme.surface,
        borderColor: state.isFocused ? theme.primary : theme.border,
        boxShadow: state.isFocused ? `0 0 0 3px rgba(76,125,255,0.20)` : "none",
        ":hover": { borderColor: theme.primary },
      }),
      singleValue: (base) => ({ ...base, color: theme.text }),
      placeholder: (base) => ({ ...base, color: theme.muted }),
      input: (base) => ({ ...base, color: theme.text }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? theme.surface2
          : state.isFocused
          ? theme.surface2
          : theme.surface,
        color: theme.text,
      }),
      indicatorSeparator: (base) => ({ ...base, backgroundColor: theme.border }),
      dropdownIndicator: (base) => ({ ...base, color: theme.muted }),
      clearIndicator: (base) => ({ ...base, color: theme.muted }),
    };

    // Quick summary
    const countryLabel =
      selectedFilters?.filter2?.length > 0 ? selectedFilters.filter2[0]?.label : "Select";
    const companyCount = selectedFilters?.filter3?.length || 0;

    return (
      <>
        <ToastContainer theme={isDark ? "dark" : "light"} />

        {/* Sticky bar */}
        <div
          className="border-bottom px-2 py-2 d-flex align-items-center gap-2"
          style={{
            backgroundColor: theme.surface,
            borderColor: theme.border,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <CustomDatePicker
              setDateFilter={setFilterDate}
              filterDate={filterDate}
              isLoading={isLoading}
              start_date_limit={start_date_limit}
              end_date_limit={end_date_limit}
              key={`date-${dashboard_name}`}
            />
          </div>

          <Button
            variant={isDark ? "outline-light" : "outline-primary"}
            className="px-3 rounded-pill d-flex align-items-center gap-2"
            onClick={() => setShowSheet(true)}
            disabled={isLoading}
            style={{
              whiteSpace: "nowrap",
              backgroundColor: isDark ? theme.btnBg : undefined,
              borderColor: isDark ? theme.btnBorder : undefined,
              color: isDark ? theme.text : undefined,
            }}
          >
            <FaSlidersH />
            Filters
          </Button>
        </div>

        {/* Summary row */}
        {dashboardChartData !== null && (
          <div
            className="px-2 py-2 border-bottom d-flex justify-content-between"
            style={{
              backgroundColor: theme.surface,
              borderColor: theme.border,
              color: theme.muted,
              fontSize: "0.85rem",
            }}
          >
            <div>{countryLabel}</div>
            <div>{companyCount} selected</div>
          </div>
        )}

        {/* Offcanvas */}
        <Offcanvas
          show={showSheet}
          onHide={() => setShowSheet(false)}
          placement="bottom"
          style={{
            height: "88%",
            backgroundColor: theme.surface,
            color: theme.text,
            paddingBottom: "60px",
          }}
        >
          <Offcanvas.Header
            closeButton
            style={{
              backgroundColor: theme.surface,
              borderBottom: `1px solid ${theme.border}`,
              color: theme.text,
            }}
          >
            <Offcanvas.Title style={{ color: theme.text }}>
              Filters & Settings
            </Offcanvas.Title>
          </Offcanvas.Header>

          <Offcanvas.Body style={{ backgroundColor: theme.surface, color: theme.text }}>
            {/* FILTERS */}
            {dashboardChartData === null ? (
              <div style={{ color: theme.muted }}>Loading filters...</div>
            ) : (
              <>
                {/* Category */}
                {filter1Values.length > 1 && (
                  <div className="mb-3">
                    <div style={{ color: theme.muted, fontSize: "0.85rem" }} className="mb-1">
                      Category
                    </div>
                    <Select
                      value={capitalizeFirstLetter(selectedFilters?.filter1)}
                      options={filter1Values}
                      onChange={handleFilter1Change}
                      isDisabled={isLoading}
                      menuPortalTarget={document.body}
                      styles={selectPortalStyles}
                      placeholder="Select Category"
                      components={autoScrollSelectComponents}
                    />
                  </div>
                )}

                {/* Countries */}
                <div className="mb-3">
                  <div style={{ color: theme.muted, fontSize: "0.85rem" }} className="mb-1">
                    Countries / Themes
                  </div>

                  {/* Wrap multiselect to give it a dark container */}
                  <div
                    style={{
                      backgroundColor: theme.surface,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 12,
                      padding: 6,
                    }}
                  >
                    <Select
                      options={filter2Values}
                      value={selectedFilters?.filter2 || []}
                       styles={selectPortalStyles}
                      onChange={handleFilter2Change}
                      labelledBy={"Select Countries"}
                      className="multi-select"
                      disabled={isLoading}
                      valueRenderer={customValueRenderer}
                      components={autoScrollSelectComponents}
                    />
                  </div>
                </div>

                {/* Companies */}
                <div className="mb-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div style={{ color: theme.muted, fontSize: "0.85rem" }} className="mb-1">
                      Companies / Keywords
                    </div>
                    <div style={{ color: theme.muted, fontSize: "0.8rem" }}>
                      Max {MAX_FILTER3}
                    </div>
                  </div>

                  <div
                    ref={filter3MenuRef}
                    style={{
                      backgroundColor: theme.surface,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 12,
                      padding: 6,
                    }}
                  >
                    <MultiSelect
                      options={filter3Values}
                      value={
                        selectedFilters?.filter3?.map((x) => capitalizeFirstLetter(x)) || []
                      }
                      onChange={handleFilter3Change}
                      labelledBy={"Select Companies"}
                      className="multi-select"
                      disabled={isLoading}
                      valueRenderer={customValueRenderer}
                      hasSelectAll={filter3Values.length < MAX_FILTER3}
                      onMenuToggle={scrollMultiSelectToSelectionOnOpen(filter3MenuRef)}
                    />
                  </div>
                </div>

                {/* Granularity */}
                {availableGranularities?.length > 1 && (
                  <div className="mb-3">
                    <div style={{ color: theme.muted, fontSize: "0.85rem" }} className="mb-1">
                      Granularity
                    </div>
                    <Select
                      value={dashboardGranularity}
                      options={granularityOptions}
                      onChange={handleGranularityChange}
                      isDisabled={isLoading}
                      menuPortalTarget={document.body}
                      styles={selectPortalStyles}
                      placeholder="Granularity"
                      components={autoScrollSelectComponents}
                    />
                  </div>
                )}
              </>
            )}

            <hr style={{ borderColor: theme.divider }} className="my-3" />

            {/* SETTINGS */}
            <div className="d-flex flex-column gap-3">
              <div className="d-flex justify-content-between align-items-center">
                <span className="d-flex align-items-center" style={{ color: theme.muted }}>
                  <i className="fa fa-lightbulb me-2" />
                  Show Suggestions
                </span>
                <Switch
                  onChange={() => setShowSuggestions(!showSuggestions)}
                  checked={!!showSuggestions}
                  height={20}
                  width={40}
                  onColor={theme.primary}
                  offColor={isDark ? "#2A3560" : "#C7CDD6"}
                />
              </div>

              {/* {typeof searchFlag !== "undefined" && typeof setSearchFlag === "function" && (
                <div className="d-flex justify-content-between align-items-center">
                  <span className="d-flex align-items-center" style={{ color: theme.muted }}>
                    <i className="fa fa-search me-2" />
                    Real-time Search
                  </span>
                  <Switch
                    onChange={() => setSearchFlag(!searchFlag)}
                    checked={!!searchFlag}
                    height={20}
                    width={40}
                    onColor={theme.primary}
                    offColor={isDark ? "#2A3560" : "#C7CDD6"}
                  />
                </div>
              )} */}

              {typeof onDownload === "function" && (
                <div className="d-grid gap-2">
                  <div style={{ color: theme.muted, fontSize: "0.85rem" }}>
                    Download Data
                  </div>

                  <div className="d-flex gap-2">
                    {["CSV", "JSON", "XLSX"].map((fmt) => (
                      <Button
                        key={fmt}
                        variant={isDark ? "outline-light" : "outline-primary"}
                        className="w-100 rounded-pill"
                        disabled={downloadButtonLoading}
                        onClick={() => onDownload(fmt)}
                        style={{
                          backgroundColor: isDark ? theme.btnBg : undefined,
                          borderColor: isDark ? theme.btnBorder : undefined,
                          color: isDark ? theme.text : undefined,
                        }}
                      >
                        {fmt}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div className="d-grid gap-2 mt-3">
                <Button
                  className="rounded-pill"
                  onClick={() => setShowSheet(false)}
                  style={{
                    backgroundColor: theme.primary,
                    borderColor: theme.primary,
                  }}
                >
                  Apply & Close
                </Button>

                <Button
                  variant={isDark ? "outline-light" : "outline-secondary"}
                  className="rounded-pill"
                  onClick={() => setShowSheet(false)}
                  style={{
                    backgroundColor: isDark ? theme.btnBg : undefined,
                    borderColor: isDark ? theme.btnBorder : undefined,
                    color: isDark ? theme.text : undefined,
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          </Offcanvas.Body>
        </Offcanvas>
      </>
    );
  }
);

export default FilterBarMobile;
